import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import { PremiumSelect } from '../../../components/PremiumSelect/PremiumSelect';
import {
  Component,
  FieldRow,
  FieldUpdate,
  formTemplateService,
  InvestorField,
  MapType,
  MatchStatus,
  openPdfBlob,
  PatternNotApplicableResult,
  TemplateSummary,
  ValidationResult,
} from '../../../services/formTemplate.service';
import { MappingCounts, MatchBadge, StatusBadge, TemplateTypeLabel } from './shared';
import { useConfirm } from './useConfirm';
import './FormTemplates.scss';

type Filter = MatchStatus | 'ALL';

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'FLAGGED', label: 'Flagged' },
  { key: 'AUTO_MATCHED', label: 'Auto-matched' },
  { key: 'CONFIRMED', label: 'Confirmed' },
  { key: 'NOT_APPLICABLE', label: 'Not applicable' },
  { key: 'ALL', label: 'All' },
];

/** How a row got its current decision (server match_source) — shown under the status badge. */
const SOURCE_LABEL: Record<string, string> = {
  AUTO_NA: 'auto (never investor data)',
  PATTERN: 'by pattern',
  ADMIN: 'admin',
  NAME_MATCH: 'name match',
  LABEL_MATCH: 'label match',
  COMPONENT: 'from component',
  LIBRARY_SEED: 'library seed',
  LIBRARY_OVERRIDE: 'library (remembered)',
};

const MAP_TYPE_HELP: Record<MapType, string> = {
  DIRECT: 'Copy the investor value as-is',
  CONSTANT: 'Always the same value (e.g. "1" to tick a box)',
  DATE_SPLIT: 'One part (dd / mm / yyyy) of an investor date, or the whole date in the form\'s own format',
  CHAR_SPLIT: 'One character per box — give the field-name prefix and box count',
  ENUM_SET: 'Tick this box when the investor value equals… (or any other value / blank)',
  PRESENCE: 'Tick this box when the investor field has a value (or when it is blank)',
  LOOKUP: 'Print a translation of the investor value (1 → YES, SAVINGS → Savings); a default when blank',
};

interface Draft {
  mapType: MapType;
  investorField: string;
  constantValue: string;
  datePart: 'dd' | 'mm' | 'yyyy' | 'full';
  /** DATE_SPLIT part=full: java date pattern, e.g. dd/MM/yyyy. */
  dateFormat: string;
  charPrefix: string;
  charCount: string;
  /** CHAR_SPLIT on a date field: pattern applied before splitting (ddMMyyyy for 8 boxes). */
  charDateFormat: string;
  enumValue: string;
  /** equals = a concrete value; other = "*" (any value not matched by a sibling box); blank = "". */
  enumMode: 'equals' | 'other' | 'blank';
  presenceWhen: 'present' | 'blank';
  /** LOOKUP: investor value → printed text rows, and what to print when the value is blank. */
  lookupRows: { from: string; to: string }[];
  lookupDefault: string;
  /** Optional guard: write this field only when guardField equals guardValue. */
  guardField: string;
  guardValue: string;
}

function parseCfg(json: string | null): any {
  try { return json ? JSON.parse(json) : {}; } catch { return {}; }
}

function draftFrom(row: FieldRow): Draft {
  const cfg = parseCfg(row.splitConfig);
  return {
    mapType: row.mapType || (row.fieldType === 'CHECKBOX' || row.fieldType === 'RADIO' ? 'ENUM_SET' : 'DIRECT'),
    investorField: row.investorField || '',
    constantValue: row.constantValue || '',
    datePart: cfg.part === 'full' ? 'full' : (cfg.part || 'dd'),
    dateFormat: cfg.format || 'dd/MM/yyyy',
    charPrefix: cfg.prefix || row.pdfField.replace(/\d+$/, ''),
    charCount: cfg.count ? String(cfg.count) : '',
    charDateFormat: cfg.dateFormat || '',
    enumValue: cfg.value && cfg.value !== '*' ? cfg.value : '',
    enumMode: cfg.value === '*' ? 'other' : (row.mapType === 'ENUM_SET' && cfg.value === '') ? 'blank' : 'equals',
    presenceWhen: cfg.when === 'blank' ? 'blank' : 'present',
    lookupRows: cfg.map && typeof cfg.map === 'object' && Object.keys(cfg.map).length
      ? Object.entries(cfg.map).map(([from, to]) => ({ from, to: String(to ?? '') }))
      : [{ from: '', to: '' }],
    lookupDefault: cfg.default ?? '',
    guardField: cfg.onlyIf?.field || '',
    guardValue: cfg.onlyIf?.equals ?? '',
  };
}

function toUpdate(fieldId: number, d: Draft): FieldUpdate {
  const u: FieldUpdate = { fieldId, action: 'MAP', mapType: d.mapType };
  switch (d.mapType) {
    case 'CONSTANT': u.constantValue = d.constantValue; break;
    case 'DATE_SPLIT': u.investorField = d.investorField;
      u.splitConfig = JSON.stringify(d.datePart === 'full' ? { part: 'full', format: d.dateFormat.trim() } : { part: d.datePart }); break;
    case 'CHAR_SPLIT': u.investorField = d.investorField;
      u.splitConfig = JSON.stringify(d.charDateFormat.trim()
        ? { prefix: d.charPrefix, count: Number(d.charCount), dateFormat: d.charDateFormat.trim() }
        : { prefix: d.charPrefix, count: Number(d.charCount) }); break;
    case 'ENUM_SET': u.investorField = d.investorField; u.splitConfig = JSON.stringify({ value: d.enumMode === 'other' ? '*' : d.enumMode === 'blank' ? '' : d.enumValue }); break;
    case 'PRESENCE': u.investorField = d.investorField; u.splitConfig = JSON.stringify({ when: d.presenceWhen }); break;
    case 'LOOKUP': {
      u.investorField = d.investorField;
      const map: Record<string, string> = {};
      d.lookupRows.forEach((r) => { if (r.from.trim()) map[r.from.trim()] = r.to; });
      u.splitConfig = JSON.stringify(d.lookupDefault.trim() ? { map, default: d.lookupDefault.trim() } : { map });
      break;
    }
    default: u.investorField = d.investorField;
  }
  if (d.guardField) {
    const cfg = u.splitConfig ? JSON.parse(u.splitConfig) : {};
    u.splitConfig = JSON.stringify({ ...cfg, onlyIf: { field: d.guardField, equals: d.guardValue } });
  }
  return u;
}

function describe(row: FieldRow, labels: Map<string, string>): string {
  if (!row.mapType) return '';
  const cfg = parseCfg(row.splitConfig);
  const base = describeBase(row, labels, cfg);
  return cfg.onlyIf?.field
    ? `${base} — only when ${labels.get(cfg.onlyIf.field) || cfg.onlyIf.field} = "${cfg.onlyIf.equals ?? ''}"`
    : base;
}

function describeBase(row: FieldRow, labels: Map<string, string>, cfg: any): string {
  const inv = row.investorField ? (labels.get(row.investorField) || row.investorField) : '';
  switch (row.mapType) {
    case 'CONSTANT':
      if (row.fieldType === 'CHECKBOX') return row.constantValue === '1' ? 'always ticked' : 'always empty';
      return row.constantValue ? `= "${row.constantValue}"` : 'always empty';
    case 'DATE_SPLIT': return cfg.part === 'full' ? `${inv} as ${cfg.format}` : `${inv} → ${cfg.part}`;
    case 'CHAR_SPLIT': return `${inv}${cfg.dateFormat ? ` as ${cfg.dateFormat}` : ''} → boxes ${cfg.prefix}01…${String(cfg.count).padStart(2, '0')}`;
    case 'ENUM_SET':
      if (cfg.value === '*') return `tick when ${inv} is any other value`;
      if (cfg.value === '') return `tick when ${inv} is blank`;
      return `tick when ${inv} = "${cfg.value}"`;
    case 'PRESENCE': return cfg.when === 'blank' ? `tick when ${inv} is blank` : `tick when ${inv} has a value`;
    case 'LOOKUP': {
      const pairs = Object.entries(cfg.map || {}).map(([k, v]) => `${k}→${v}`).join(', ');
      return `${inv}: ${pairs}${cfg.default !== undefined ? ` (blank→${cfg.default})` : ''}`;
    }
    default: return inv;
  }
}

/**
 * Admin mapping screen — the one manual step of SP onboarding. Lists every PDF field with
 * its match status (default filter: flagged), lets the admin pick the investor field and
 * how it maps, then validate → preview → activate. Rendered inside MainLayout.
 */
export const FormTemplateMapping: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const templateId = Number(id);
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirm();

  const [template, setTemplate] = useState<TemplateSummary | null>(null);
  const [fields, setFields] = useState<FieldRow[]>([]);
  const [catalog, setCatalog] = useState<InvestorField[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<Filter>('FLAGGED');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [previewCode, setPreviewCode] = useState('');
  const [patternText, setPatternText] = useState('');
  const [seedDiffers, setSeedDiffers] = useState(0);
  const [patternPreview, setPatternPreview] = useState<PatternNotApplicableResult | null>(null);
  const [showPatternList, setShowPatternList] = useState(false);

  const editable = template ? ['DISCOVERED', 'MAPPING_IN_PROGRESS', 'VALIDATED'].includes(template.status) : false;
  const labels = useMemo(() => new Map(catalog.map((f) => [f.name, f.label])), [catalog]);
  const groups = useMemo(() => {
    const g = new Map<string, InvestorField[]>();
    catalog.forEach((f) => g.set(f.group, [...(g.get(f.group) || []), f]));
    return Array.from(g.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [catalog]);
  // Searchable picker: 100+ fields, so the group is folded into the label ("Personal › First name")
  // and stays visible/searchable; the closed control shows just the field label.
  const fieldOptions = useMemo(() => groups.flatMap(([group, items]) =>
    items.map((f) => ({ value: f.name, label: `${group} › ${f.label}`, selectedLabel: f.label }))), [groups]);
  const dateFieldOptions = useMemo(() => groups.flatMap(([group, items]) =>
    items.filter((f) => f.date).map((f) => ({ value: f.name, label: `${group} › ${f.label}`, selectedLabel: f.label }))), [groups]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [detail, inv] = await Promise.all([
        formTemplateService.detail(templateId),
        catalog.length ? Promise.resolve(catalog) : formTemplateService.investorFields(),
      ]);
      setTemplate(detail.template);
      setFields(detail.fields);
      setSeedDiffers(detail.seedDiffers || 0);
      setCatalog(inv);
      if (detail.template.assembled) setComponents(await formTemplateService.components(templateId));
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load template');
      navigate('/admin/form-templates');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return fields.filter((f) =>
      (filter === 'ALL' || f.matchStatus === filter)
      && (!q || f.pdfField.toLowerCase().includes(q) || (f.nearbyText || '').toLowerCase().includes(q)
        || (f.investorField || '').toLowerCase().includes(q)));
  }, [fields, filter, search]);

  useEffect(() => {
    formTemplateService.autoNaPatterns()
      .then((p) => setPatternText((cur) => cur || p.join(', ')))
      .catch(() => { /* box stays empty; the admin can still type patterns */ });
  }, []);

  const patternList = () => patternText.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);

  const handlePatternPreview = () => run('Pattern preview', async () => {
    const r = await formTemplateService.notApplicableByPattern(templateId, patternList(), true);
    setPatternPreview(r);
    setShowPatternList(r.matched > 0 && r.matched <= 40);
  }, false);

  const handlePatternApply = async () => {
    const list = patternList();
    if (!list.length) { toast.warn('Enter at least one pattern'); return; }
    const preview = patternPreview ?? await formTemplateService.notApplicableByPattern(templateId, list, true);
    if (preview.matched === 0) { toast.info('No flagged field matches these patterns'); return; }
    if (await confirm('Mark as not applicable',
      `Mark ${preview.matched} of ${preview.flaggedBefore} flagged field(s) as not applicable? Already mapped fields are not touched; any of them can be mapped later.`, 'Mark')) {
      run('Mark by pattern', async () => {
        const r = await formTemplateService.notApplicableByPattern(templateId, list, false);
        setPatternPreview(null);
        toast.success(`${r.matched} field(s) marked not applicable`);
      });
    }
  };

  const run = async (label: string, action: () => Promise<unknown>, reload = true) => {
    try {
      setBusy(true);
      await action();
      if (reload) await load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || `${label} failed`);
    } finally {
      setBusy(false);
    }
  };

  const apply = (updates: FieldUpdate[]) => run('Update', async () => {
    setTemplate(await formTemplateService.updateFields(templateId, updates));
    setEditing(null);
    setDraft(null);
  });

  const saveDraft = () => {
    if (editing == null || !draft) return;
    if (draft.mapType !== 'CONSTANT' && !draft.investorField) { toast.warn('Pick an investor field'); return; }
    if (draft.mapType === 'LOOKUP' && !draft.lookupRows.some((r) => r.from.trim())) { toast.warn('Add at least one value → text row'); return; }
    if (draft.mapType === 'DATE_SPLIT' && draft.datePart === 'full' && !draft.dateFormat.trim()) { toast.warn('Enter a date format, e.g. dd/MM/yyyy'); return; }
    if (draft.mapType === 'CHAR_SPLIT' && (!draft.charPrefix || !Number(draft.charCount))) { toast.warn('Character split needs a prefix and a box count'); return; }
    if (draft.mapType === 'ENUM_SET' && draft.enumMode === 'equals' && !draft.enumValue) { toast.warn('Give the value that ticks this box'); return; }
    apply([toUpdate(editing, draft)]);
  };

  const handleValidate = () => run('Validation', async () => {
    const v = await formTemplateService.validate(templateId);
    setValidation(v);
    if (v.valid) toast.success('Mapping is valid'); else toast.error(`${v.errors.length} error(s) found`);
  });

  const handlePreview = () => run('Preview', async () => {
    toast.info('Generating preview…');
    openPdfBlob(await formTemplateService.preview(templateId, previewCode.trim() || undefined), `preview-${templateId}.pdf`);
  }, false);

  const handleActivate = async () => {
    if (!template) return;
    const ok = await confirm('Activate template',
      `Activate "${template.name}" v${template.version} for ${template.spName || template.spRef}? Any previously active template of the same type is retired, and investors of this provider get this form from now on.`,
      'Activate');
    if (!ok) return;
    run('Activation', async () => {
      await formTemplateService.activate(templateId);
      toast.success('Template is now ACTIVE');
    });
  };

  if (loading || !template) {
    return <div className="form-templates"><LoadingSpinner /></div>;
  }

  const total = template.confirmed + template.autoMatched + template.flagged + template.notApplicable;
  const pct = total ? Math.round(((total - template.flagged) / total) * 100) : 0;

  return (
    <div className="form-templates">
      {dialog}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h1 className="dashboard-title-modern mb-0">
            {template.name} <span className="text-muted fw-normal fs-6">v{template.version}</span>
          </h1>
          <div className="text-muted small">
            {template.spName || template.spRef} · {TemplateTypeLabel[template.templateType]} · {template.pageCount} pages
            · {template.fieldCount} fields <StatusBadge status={template.status} />
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/admin/form-templates')}>
            <i className="bi bi-arrow-left me-1" />All templates
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={handleValidate} disabled={busy}>Validate</button>
          <button className="btn btn-outline-secondary btn-sm" onClick={handlePreview} disabled={busy}>
            {previewCode.trim() ? 'Preview with investor data' : 'Preview with sample data'}
          </button>
          {template.status !== 'ACTIVE' && (
            <button className="btn btn-success btn-sm" onClick={handleActivate} disabled={busy || template.flagged > 0}
              title={template.flagged > 0 ? 'Map or dismiss every flagged field first' : ''}>
              Activate
            </button>
          )}
        </div>
      </div>

      {!editable && (
        <div className="alert alert-warning py-2 small">
          This template is {template.status.toLowerCase()} and read-only. Upload a new version to change its mapping.
        </div>
      )}

      <div className="card p-3 mb-3">
        <div className="progress mb-2" style={{ height: 8 }}>
          <div className="progress-bar bg-success" style={{ width: `${pct}%` }} />
        </div>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <MappingCounts t={template} verbose />
            <span className="text-muted small ms-2">{pct}% resolved</span>
          </div>
          {editable && (
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-outline-primary" disabled={busy || template.autoMatched === 0}
                onClick={() => run('Confirm', () => formTemplateService.confirmAuto(templateId))}>
                Confirm all auto-matched
              </button>
              {(template.assembled || template.recognisedLibrary) && (
                <button className="btn btn-sm btn-outline-primary" disabled={busy || template.flagged === 0}
                  title={template.assembled
                    ? 'Pull mappings, not-applicable decisions and remembered library decisions from the components into the flagged rows'
                    : `Pull the ${template.recognisedLibrary} library seed and remembered decisions into the flagged rows`}
                  onClick={() => run('Re-apply', async () => {
                    const before = template.flagged;
                    const after = await formTemplateService.reseed(templateId);
                    toast.success(`${before - after.flagged} flagged field(s) resolved from the ${template.assembled ? 'components' : 'library seed'}`);
                  })}>
                  {template.assembled ? 'Re-apply component mappings' : 'Re-apply library seed'}
                </button>
              )}
              {seedDiffers > 0 && (
                <span className="badge bg-warning-subtle text-warning-emphasis align-self-center"
                  title="Rows marked with a seed hint below: the library seed maps them differently. Reset the row, then Re-apply, to take the seed.">
                  {seedDiffers} differ from the library seed
                </span>
              )}
              <button className="btn btn-sm btn-outline-secondary" disabled={busy || template.flagged === 0}
                onClick={async () => {
                  if (await confirm('Mark as not applicable',
                    `Mark all ${template.flagged} flagged fields as not applicable? They stay blank in the filled PDF.`, 'Mark all')) {
                    run('Dismiss', () => formTemplateService.markNotApplicable(templateId));
                  }
                }}>
                Mark remaining flagged as not applicable
              </button>
            </div>
          )}
        </div>
        {editable && template.flagged > 0 && (
          <div className="form-templates__pattern mt-3">
            <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2">
              <label className="form-label mb-0 fw-semibold" htmlFor="na-patterns">Mark not applicable by field-name pattern</label>
              <small className="text-muted">
                <code>*</code> = anything, <code>?</code> = one character · comma or space separated · only flagged rows are affected
              </small>
            </div>
            <div className="d-flex gap-2 align-items-start mt-1 flex-wrap">
              <textarea id="na-patterns" className="form-control form-control-sm form-templates__pattern-input" rows={2}
                value={patternText} onChange={(e) => { setPatternText(e.target.value); setPatternPreview(null); }}
                placeholder="*_signature, office_use_*, *second_holder*, guardian*" />
              <button className="btn btn-sm btn-outline-secondary" disabled={busy || !patternText.trim()} onClick={handlePatternPreview}>Preview</button>
              <button className="btn btn-sm btn-dark" disabled={busy || !patternText.trim()} onClick={handlePatternApply}>Mark N/A</button>
            </div>
            {patternPreview && !patternPreview.applied && (
              <div className="small mt-2">
                <span className={patternPreview.matched ? 'text-success' : 'text-muted'}>
                  Matches <strong>{patternPreview.matched}</strong> of {patternPreview.flaggedBefore} flagged field(s)
                  {patternPreview.matched > 0 && ` — ${patternPreview.flaggedBefore - patternPreview.matched} would remain for review.`}
                </span>
                {patternPreview.matched > 0 && (
                  <button className="btn btn-link btn-sm p-0 ms-2 align-baseline" onClick={() => setShowPatternList((v) => !v)}>
                    {showPatternList ? 'hide list' : 'show list'}
                  </button>
                )}
                {showPatternList && (
                  <div className="form-templates__pattern-list mt-1">
                    {patternPreview.fields.map((f) => <code key={f} className="me-2">{f}</code>)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {components.length > 0 && (
          <div className="mt-2 small">
            <span className="text-muted me-1">Assembled from:</span>
            {components.map((c) => (
              <span key={c.id} className="badge bg-light text-dark me-1">
                {c.sequence}. {c.displayName || c.sourceRef} <em className="text-muted">({c.fieldPrefix})</em>
              </span>
            ))}
          </div>
        )}
      </div>

      {validation && (
        <div className={`card p-3 mb-3 border-start border-4 ${validation.valid ? 'border-success' : 'border-danger'}`}>
          <h2 className="form-templates__h2">{validation.valid ? 'Validation passed' : 'Validation failed'}</h2>
          <div className="small">{validation.summary}</div>
          {validation.errors.length > 0 && (
            <ul className="small text-danger mb-0 mt-2">{validation.errors.map((e) => <li key={e}>{e}</li>)}</ul>
          )}
          {validation.warnings.length > 0 && (
            <ul className="small text-warning-emphasis mb-0 mt-2">{validation.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
          )}
        </div>
      )}

      <div className="card p-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <div className="btn-group btn-group-sm" role="group">
            {FILTERS.map((f) => (
              <button key={f.key} type="button"
                className={`btn ${filter === f.key ? 'btn-dark' : 'btn-outline-secondary'}`}
                onClick={() => setFilter(f.key)}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <input className="form-control form-control-sm form-templates__search" placeholder="Search PDF field / label"
              value={search} onChange={(e) => setSearch(e.target.value)} />
            <input className="form-control form-control-sm form-templates__search" placeholder="…or enter an investor code to preview with real data"
              value={previewCode} onChange={(e) => setPreviewCode(e.target.value)} />
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-sm align-middle form-templates__fields">
            <thead>
              <tr>
                <th>PDF field</th>
                <th>Type</th>
                <th>Page</th>
                <th>Label near field</th>
                <th>Status</th>
                <th>Mapped to</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={7} className="text-muted">
                  {filter === 'FLAGGED' ? 'Nothing is flagged — every field is mapped or dismissed.' : 'No fields match this filter.'}
                </td></tr>
              )}
              {visible.map((row) => (
                <React.Fragment key={row.id}>
                  <tr className={editing === row.id ? 'table-active' : ''}>
                    <td><code>{row.pdfField}</code></td>
                    <td>{row.fieldType}</td>
                    <td>{row.page ?? '—'}</td>
                    <td className="text-muted">{row.nearbyText || row.suggestedName || '—'}</td>
                    <td><MatchBadge status={row.matchStatus} /></td>
                    <td>
                      {describe(row, labels) || <span className="text-muted">—</span>}
                      {row.matchSource && <><br /><small className="text-muted">{SOURCE_LABEL[row.matchSource] || row.matchSource}</small></>}
                      {row.seedSuggestion && (
                        <><br /><small className="text-warning-emphasis" title="The library seed maps this field differently. Reset the row, then Re-apply, to take the seed.">
                          seed: {row.seedSuggestion}{row.matchStatus === 'FLAGGED' ? ' · Re-apply to use it' : ' · Reset, then Re-apply'}
                        </small></>
                      )}
                    </td>
                    <td className="text-nowrap">
                      {editable && editing !== row.id && (
                        <>
                          <button className="btn btn-sm btn-primary me-1" onClick={() => { setEditing(row.id); setDraft(draftFrom(row)); }}>
                            {row.mapType ? 'Edit' : 'Map'}
                          </button>
                          {row.matchStatus === 'AUTO_MATCHED' && (
                            <button className="btn btn-sm btn-outline-success me-1" disabled={busy}
                              onClick={() => apply([{ fieldId: row.id, action: 'CONFIRM' }])}>OK</button>
                          )}
                          {row.matchStatus !== 'NOT_APPLICABLE' && (
                            <button className="btn btn-sm btn-outline-secondary me-1" disabled={busy} title="Not applicable — leave blank"
                              onClick={() => apply([{ fieldId: row.id, action: 'NOT_APPLICABLE' }])}>N/A</button>
                          )}
                          {row.matchStatus !== 'FLAGGED' && (
                            <button className="btn btn-sm btn-link" disabled={busy}
                              onClick={() => apply([{ fieldId: row.id, action: 'RESET' }])}>Reset</button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  {editing === row.id && draft && (
                    <tr className="table-active">
                      <td colSpan={7}>
                        <div className="row g-2 align-items-start form-templates__editor">
                          <div className="col-md-3">
                            <label className="form-label">How it maps</label>
                            <select className="form-select form-select-sm" value={draft.mapType}
                              onChange={(e) => setDraft({ ...draft, mapType: e.target.value as MapType })}>
                              {(Object.keys(MAP_TYPE_HELP) as MapType[]).map((m) => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
                            </select>
                            <div className="form-text">{MAP_TYPE_HELP[draft.mapType]}</div>
                          </div>

                          {draft.mapType === 'CONSTANT' ? (
                            <div className="col-md-3">
                              <label className="form-label">Constant value</label>
                              {row.fieldType === 'CHECKBOX' ? (
                                <select className="form-select form-select-sm" value={draft.constantValue === '1' ? '1' : ''}
                                  onChange={(e) => setDraft({ ...draft, constantValue: e.target.value })}>
                                  <option value="">Not ticked (leave empty)</option>
                                  <option value="1">Ticked</option>
                                </select>
                              ) : (
                                <input className="form-control form-control-sm" value={draft.constantValue}
                                  onChange={(e) => setDraft({ ...draft, constantValue: e.target.value })}
                                  placeholder="text (leave empty for a blank box)" />
                              )}
                              <div className="form-text">&nbsp;</div>
                            </div>
                          ) : (
                            <div className="col-md-3">
                              <label className="form-label">Investor field</label>
                              <PremiumSelect
                                value={draft.investorField}
                                onChange={(val) => setDraft({ ...draft, investorField: val })}
                                options={draft.mapType === 'DATE_SPLIT' ? dateFieldOptions : fieldOptions}
                                placeholder="— choose —"
                                searchable
                              />
                              <div className="form-text">&nbsp;</div>
                            </div>
                          )}

                          {draft.mapType === 'DATE_SPLIT' && (
                            <div className="col-md-2">
                              <label className="form-label">Date part</label>
                              <select className="form-select form-select-sm" value={draft.datePart}
                                onChange={(e) => setDraft({ ...draft, datePart: e.target.value as Draft['datePart'] })}>
                                <option value="dd">Day (dd)</option>
                                <option value="mm">Month (mm)</option>
                                <option value="yyyy">Year (yyyy)</option>
                                <option value="full">Whole date, formatted</option>
                              </select>
                              <div className="form-text">&nbsp;</div>
                            </div>
                          )}
                          {draft.mapType === 'DATE_SPLIT' && draft.datePart === 'full' && (
                            <div className="col-md-2">
                              <label className="form-label">Date format</label>
                              <input className="form-control form-control-sm" value={draft.dateFormat} list="date-format-hints"
                                onChange={(e) => setDraft({ ...draft, dateFormat: e.target.value })} placeholder="dd/MM/yyyy" />
                              <datalist id="date-format-hints">
                                <option value="dd/MM/yyyy" /><option value="dd-MM-yyyy" /><option value="ddMMyyyy" /><option value="MM/yyyy" /><option value="dd MMM yyyy" />
                              </datalist>
                              <div className="form-text">dd = day, MM = month, yyyy = year</div>
                            </div>
                          )}
                          {draft.mapType === 'CHAR_SPLIT' && (
                            <>
                              <div className="col-md-2">
                                <label className="form-label">Field-name prefix</label>
                                <input className="form-control form-control-sm" value={draft.charPrefix}
                                  onChange={(e) => setDraft({ ...draft, charPrefix: e.target.value })} />
                                <div className="form-text">Boxes are prefix + 01, 02, …</div>
                              </div>
                              <div className="col-md-2">
                                <label className="form-label">Number of boxes</label>
                                <input className="form-control form-control-sm" type="number" min={1} value={draft.charCount}
                                  onChange={(e) => setDraft({ ...draft, charCount: e.target.value })} />
                                <div className="form-text">&nbsp;</div>
                              </div>
                              {catalog.find((f) => f.name === draft.investorField)?.date && (
                                <div className="col-md-2">
                                  <label className="form-label">Date format first</label>
                                  <input className="form-control form-control-sm" value={draft.charDateFormat}
                                    onChange={(e) => setDraft({ ...draft, charDateFormat: e.target.value })} placeholder="ddMMyyyy" />
                                  <div className="form-text">e.g. ddMMyyyy for 8 boxes</div>
                                </div>
                              )}
                            </>
                          )}
                          {draft.mapType === 'PRESENCE' && (
                            <div className="col-md-3">
                              <label className="form-label">Tick when the value is</label>
                              <select className="form-select form-select-sm" value={draft.presenceWhen}
                                onChange={(e) => setDraft({ ...draft, presenceWhen: e.target.value as Draft['presenceWhen'] })}>
                                <option value="present">present (any value)</option>
                                <option value="blank">blank (no value)</option>
                              </select>
                              <div className="form-text">e.g. passport box ← passport number</div>
                            </div>
                          )}
                          {draft.mapType === 'LOOKUP' && (
                            <div className="col-md-5">
                              <label className="form-label">Investor value → printed text</label>
                              {draft.lookupRows.map((r, i) => (
                                <div className="d-flex gap-1 mb-1 align-items-center" key={i}>
                                  <input className="form-control form-control-sm" placeholder="stored value (e.g. 1)" value={r.from}
                                    onChange={(e) => setDraft({ ...draft, lookupRows: draft.lookupRows.map((x, j) => j === i ? { ...x, from: e.target.value } : x) })} />
                                  <span className="text-muted">→</span>
                                  <input className="form-control form-control-sm" placeholder="printed (e.g. YES)" value={r.to}
                                    onChange={(e) => setDraft({ ...draft, lookupRows: draft.lookupRows.map((x, j) => j === i ? { ...x, to: e.target.value } : x) })} />
                                  <button type="button" className="btn btn-sm btn-outline-secondary" title="Remove row" disabled={draft.lookupRows.length === 1}
                                    onClick={() => setDraft({ ...draft, lookupRows: draft.lookupRows.filter((_, j) => j !== i) })}>×</button>
                                </div>
                              ))}
                              <div className="d-flex gap-2 align-items-center mt-1">
                                <button type="button" className="btn btn-sm btn-link p-0"
                                  onClick={() => setDraft({ ...draft, lookupRows: [...draft.lookupRows, { from: '', to: '' }] })}>+ add row</button>
                                <label className="form-label mb-0 ms-auto small text-nowrap">When blank print</label>
                                <input className="form-control form-control-sm" style={{ maxWidth: '8rem' }} placeholder="(nothing)" value={draft.lookupDefault}
                                  onChange={(e) => setDraft({ ...draft, lookupDefault: e.target.value })} />
                              </div>
                              <div className="form-text">Unmatched values print as stored. Matching ignores case.</div>
                            </div>
                          )}
                          {draft.mapType === 'ENUM_SET' && (
                            <div className="col-md-3">
                              <label className="form-label">Tick when the value</label>
                              <select className="form-select form-select-sm" value={draft.enumMode}
                                onChange={(e) => setDraft({ ...draft, enumMode: e.target.value as Draft['enumMode'] })}>
                                <option value="equals">equals…</option>
                                <option value="other">is any other value (the "Others" box)</option>
                                <option value="blank">is blank</option>
                              </select>
                              {draft.enumMode === 'equals' ? (
                                <input className="form-control form-control-sm mt-1" value={draft.enumValue}
                                  onChange={(e) => setDraft({ ...draft, enumValue: e.target.value })}
                                  placeholder='e.g. "Male", "Married", "Passport"' />
                              ) : null}
                              <div className="form-text">{draft.enumMode === 'other'
                                ? 'Ticks when the field has a value that no sibling box lists'
                                : row.options ? `PDF options: ${row.options}` : '\u00a0'}</div>
                            </div>
                          )}

                          <div className="col-md-3">
                            <label className="form-label">Only when <span className="text-muted">(optional)</span></label>
                            <PremiumSelect
                              value={draft.guardField}
                              onChange={(val) => setDraft({ ...draft, guardField: val, guardValue: val ? draft.guardValue : '' })}
                              options={[{ value: '', label: 'Always' }, ...fieldOptions]}
                              placeholder="Always"
                              searchable
                            />
                            {draft.guardField ? (
                              <input className="form-control form-control-sm mt-1" value={draft.guardValue}
                                onChange={(e) => setDraft({ ...draft, guardValue: e.target.value })}
                                placeholder='equals… e.g. "no"' />
                            ) : (
                              <div className="form-text">Write this field only when another field equals a value</div>
                            )}
                          </div>

                          <div className="col-md-auto">
                            <label className="form-label d-block">&nbsp;</label>
                            <div className="d-flex gap-2">
                              <button className="btn btn-sm btn-primary" onClick={saveDraft} disabled={busy}>Save mapping</button>
                              <button className="btn btn-sm btn-outline-secondary" onClick={() => { setEditing(null); setDraft(null); }}>Cancel</button>
                            </div>
                            <div className="form-text">&nbsp;</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
